/**
 * ONE REVIEW OF THE SHOP — a review the business transcribed from its Google
 * listing (or entered itself), as the product page and the "See all" sheet
 * both paint it.
 *
 * It wears the product review card's surface (`reviewCard.module.css`: the
 * radius, the hairline, the glass, the footer with the avatar and the byline)
 * so the two kinds of review sit together as one family — but it is NOT a
 * `ReviewCard`. That card is about an item (a thumbnail, a link to the
 * product, a Helpful vote on a row customers can vote on); this one is about
 * the shop, and the one thing it carries that the other does not is its
 * SOURCE, printed on every card: "From Google", or "Entered by the shop".
 * Nothing on it says "verified purchase" — the server has no such column and
 * the page invents no such word.
 *
 * The byline is `<bdi>` per half for the same reason `ReviewCard` isolates
 * its own: "דנה · 4 days ago" as one run puts the dot on the wrong side.
 */
import React from 'react';
import { StarsRow } from './ReviewCard';
import { formatReviewTime } from './reviewTime';
import GoogleMark from './GoogleMark';
import cardStyles from './reviewCard.module.css';
import styles from './shopReviewsBlock.module.css';

/** The label a card prints for where its review came from. Unknown reads as the shop's. */
export function sourceLabel(source, t) {
  if (source === 'GOOGLE_TRANSCRIBED') return t('shopReviews.fromGoogle', { defaultValue: 'From Google' });
  if (source === 'STYLON') return t('shopReviews.fromStylon', { defaultValue: 'Through Stylon' });
  return t('shopReviews.fromShop', { defaultValue: 'Entered by the shop' });
}

/**
 * WHERE THIS REVIEW CAME FROM — a mark and the words, never the mark alone.
 *
 * The owner asked for "a Google icon or something" on the imported reviews.
 * The icon is the fast read; the words are the accessible one and the honest
 * one, so both are always printed. A Stylon row gets a plain initial rather
 * than a logo: this storefront has no monochrome Stylon mark that survives
 * all three background modes, and inventing one here would be a second
 * definition of the brand to keep in step.
 */
export function SourceBadge({ source, t, testId }) {
  return (
    <span className={styles.source} data-testid={testId} data-source={source}>
      {source === 'GOOGLE_TRANSCRIBED' && (
        <GoogleMark
          className={styles.sourceMark}
          testId={testId ? `${testId.replace(/-source$/, '')}-google-mark` : undefined}
        />
      )}
      {source === 'STYLON' && <span className={styles.stylonMark} aria-hidden>S</span>}
      <span>{sourceLabel(source, t)}</span>
    </span>
  );
}

/** "{{n}} reviews on Google" only when every one of them is; otherwise "{{n}} reviews". */
export function shopReviewsCountLine(shopReviews, t) {
  const count = Number(shopReviews?.count) || 0;
  const google = Number(shopReviews?.sources?.GOOGLE_TRANSCRIBED) || 0;
  if (count > 0 && google === count) {
    return t('shopReviews.countOnGoogle', { defaultValue: '{{n}} reviews on Google', n: count, count });
  }
  return t('shopReviews.count', { defaultValue: '{{n}} reviews', n: count, count });
}

export function allFromGoogle(shopReviews) {
  const count = Number(shopReviews?.count) || 0;
  return count > 0 && (Number(shopReviews?.sources?.GOOGLE_TRANSCRIBED) || 0) === count;
}

const ShopReviewCard = ({ review, t, locale, testIdPrefix = 'shop-review' }) => {
  if (!review) return null;
  const name = String(review.authorName || '').trim() || '—';
  const initial = name.charAt(0) || '·';
  const when = review.reviewedAt ? formatReviewTime(review.reviewedAt, locale) : '';

  return (
    <article
      className={`${cardStyles.card} ${styles.card}`}
      data-testid={`${testIdPrefix}-${review.id}`}
      data-source={review.source}
    >
      <div className={styles.cardHead}>
        <StarsRow value={review.rating} t={t} testId={`${testIdPrefix}-stars`} />
        <SourceBadge source={review.source} t={t} testId={`${testIdPrefix}-source`} />
      </div>

      {review.text && (
        <p className={cardStyles.body} data-testid={`${testIdPrefix}-body`}>{review.text}</p>
      )}

      <div className={cardStyles.footer}>
        <span className={cardStyles.avatar} aria-hidden>
          <span>{initial}</span>
        </span>
        <span className={cardStyles.byline} data-testid={`${testIdPrefix}-byline`}>
          <bdi>{name}</bdi>
          {when && <span className={cardStyles.bylineDot} aria-hidden>·</span>}
          {when && <bdi>{when}</bdi>}
        </span>
      </div>
    </article>
  );
};

export default ShopReviewCard;
